import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { AnalyticsStrip, PeriodSelect } from "@/components/admin/AnalyticsStrip";

const BRAND_META = {
  clinic: {
    name: "Royal Skin Cosmetic Clinic", kind: "Aesthetic Clinic · Dubai", icon: Sparkles,
    accent: "#D4AF37", home: "/admin/overview",
    stat: (b) => [
      ["Enquiries", b.enquiries_total, `${b.enquiries_new} new`],
      ["Leads", b.leads], ["Treatments", b.treatments], ["Articles", b.articles],
    ],
  },
  docstate: {
    name: "Docstate Properties", kind: "Real Estate · Dubai", icon: Building2,
    accent: "#C9A24B", home: "/admin/re",
    stat: (b) => [
      ["Listings", b.listings_total, `${b.listings_published} live`],
      ["Agents", b.agents], ["New Viewings", b.viewings_new], ["Brokers Pending", b.brokers_pending],
    ],
  },
};

export const SuperDashboard = ({ onEnterBrand }) => {
  const [brands, setBrands] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [analytics, setAnalytics] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/admin/super/dashboard").then((r) => setBrands(r.data.brands)).catch(() => setBrands([])); }, []);
  useEffect(() => { api.get(`/admin/super/analytics?period=${period}`).then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null)); }, [period]);

  const metricsBySlug = useMemo(() => {
    const m = {};
    (analytics?.brands || []).forEach((b) => { m[b.slug] = b; });
    return m;
  }, [analytics]);

  const enter = (slug) => {
    onEnterBrand?.(slug);
    navigate(BRAND_META[slug]?.home || "/admin/overview");
  };

  if (brands === null) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#C9A24B]" /></div>;

  return (
    <div data-testid="super-dashboard">
      <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#B38B2D]">Platform Control</p>
      <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-3xl font-semibold text-[#1A1615]">All Brands</h2>
          <p className="mt-1 text-sm text-[#91837C]">Oversee every company on the platform. Choose a brand to manage its backoffice.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Performance</span>
          <PeriodSelect value={period} onChange={setPeriod} testid="super-analytics-period" />
        </div>
      </div>
      <div className="mt-7 grid gap-5 lg:grid-cols-2">
        {brands.map((b) => {
          const meta = BRAND_META[b.slug] || BRAND_META.clinic;
          const Icon = meta.icon;
          return (
            <div key={b.slug} className="glass overflow-hidden rounded-3xl" data-testid={`super-brand-${b.slug}`}>
              <div className="flex items-center gap-3 border-b border-[#E8E2D9] px-6 py-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: `${meta.accent}1a`, color: meta.accent }}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate font-serif text-lg font-semibold text-[#1A1615]">{meta.name}</p>
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#91837C]">{meta.kind}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-px bg-[#E8E2D9] sm:grid-cols-4">
                {meta.stat(b).map(([label, val, sub]) => (
                  <div key={label} className="bg-[#FAF8F5] px-4 py-4 text-center">
                    <p className="font-serif text-2xl font-semibold text-[#1A1615]">{val ?? 0}</p>
                    <p className="mt-0.5 text-[10px] uppercase tracking-[0.1em] text-[#91837C]">{label}</p>
                    {sub && <p className="text-[10px] text-[#B38B2D]">{sub}</p>}
                  </div>
                ))}
              </div>
              {metricsBySlug[b.slug] && (
                <>
                  <p className="border-t border-[#E8E2D9] bg-white px-6 pt-3 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B38B2D]">Performance · {analytics?.period === "all" ? "all time" : analytics?.period}</p>
                  <AnalyticsStrip metrics={metricsBySlug[b.slug]} accent={meta.accent} testid={`super-analytics-${b.slug}`} />
                </>
              )}
              <div className="px-6 py-4">
                <button onClick={() => enter(b.slug)} data-testid={`super-enter-${b.slug}`}
                  className="btn-gold w-full px-5 py-3 text-xs">
                  Manage {meta.name.split(" ")[0]} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
